import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';

import { ApiError } from '@/shared/api/client';
import {
  brokers,
  commissions,
  type Commission,
  type CommissionStatus,
  type ManualCommissionInput,
} from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import { baixarCsv, moedaParaPlanilha } from '@/shared/lib/exportar';
import {
  applyRate,
  formatMoney,
  formatRate,
  moneyInputError,
  parseMoney,
  rateInputError,
  rateToBasisPoints,
  toMoney,
} from '@/shared/lib/format';
import { Alert } from '@/shared/ui/Alert';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { Card } from '@/shared/ui/Card';
import { DataTable, type Column } from '@/shared/ui/DataTable';
import { EmptyState } from '@/shared/ui/EmptyState';
import { KpiCard } from '@/shared/ui/KpiCard';
import { ConfirmDialog, Modal } from '@/shared/ui/Modal';
import { MoneyField } from '@/shared/ui/MoneyField';
import { PageHeader } from '@/shared/ui/PageHeader';
import { Pagination } from '@/shared/ui/Pagination';
import { Field } from '@/shared/ui/Field';
import { Select } from '@/shared/ui/Select';
import { StatusBadge } from '@/shared/ui/StatusBadge';
import { statusLabel } from '@/shared/ui/statusLabels';
import { Textarea } from '@/shared/ui/Textarea';
import { useToast } from '@/shared/ui/useToast';

import { fieldErrorOf } from '../shared/fieldError';

import { PoliciesPage } from './PoliciesPage';

// A lista de apolices mora no proprio arquivo (P-APO-1); continua saindo daqui para
// quem ja importava deste modulo.
export { PoliciesPage };

const limite = 25;

/**
 * Comissoes.
 *
 * Recebida e o dinheiro que entrou da seguradora; paga e o que saiu para o
 * corretor. A tela separa os dois porque e justamente esse intervalo que o
 * financeiro acompanha.
 *
 * Os totais vem do servidor. Somar apenas o que esta na pagina mostraria um
 * numero que muda ao virar a pagina.
 */
export function CommissionsPage() {
  const [lancando, setLancando] = useState(false);
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [status, setStatus] = useState('');
  const [competencia, setCompetencia] = useState('');
  const [offset, setOffset] = useState(0);
  const [baixando, setBaixando] = useState<{
    commission: Commission;
    target: CommissionStatus;
  } | null>(null);

  const podeBaixar = can('comissoes.comissao.transition');
  const podeLancar = can('comissoes.comissao.create');

  const lancar = useMutation({
    mutationFn: (input: ManualCommissionInput) => commissions.launch(input),
    onSuccess: () => {
      toast.success('Comissao lancada.');
      setLancando(false);
      void queryClient.invalidateQueries({ queryKey: ['commissions'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });

  const consulta = useQuery({
    queryKey: ['commissions', { status, competencia, offset }],
    queryFn: ({ signal }) =>
      commissions.list(
        { status, competence: competencia, limit: limite, offset },
        signal,
      ),
  });

  const baixar = useMutation({
    mutationFn: ({ id, target }: { id: string; target: CommissionStatus }) =>
      commissions.settle(id, target),
    onSuccess: () => {
      toast.success('Repasse atualizado.');
      setBaixando(null);
      void queryClient.invalidateQueries({ queryKey: ['commissions'] });
    },
    onError: (error: Error) => {
      toast.error(error.message);
      setBaixando(null);
    },
  });

  const totais = consulta.data?.totals;

  const colunas: Column<Commission>[] = [
    {
      key: 'origem',
      header: 'Apolice',
      render: (commission) =>
        commission.policy_id ? (
          <div className="min-w-0">
            <p className="truncate text-ink-900">{commission.policyholder_label}</p>
            <p className="truncate text-2xs text-ink-500">
              Apolice {commission.policy_number}
            </p>
          </div>
        ) : (
          <div className="min-w-0">
            <p className="truncate text-ink-900">Lancamento manual</p>
            <p className="truncate text-2xs text-ink-500">{commission.manual_reason}</p>
          </div>
        ),
    },
    {
      key: 'seguradora',
      header: 'Emissor',
      hideBelow: 'lg',
      render: (commission) =>
        commission.issuer_label || <span className="text-ink-400">sem apolice</span>,
    },
    {
      key: 'corretor',
      header: 'Corretor',
      render: (commission) => (
        <div className="min-w-0">
          <p className="truncate font-medium text-ink-900">
            {commission.broker_label || 'Operacao direta'}
          </p>
          <p className="truncate text-2xs text-ink-500">
            Competencia {commission.competence}
          </p>
        </div>
      ),
    },
    {
      key: 'base',
      header: 'Premio',
      align: 'right',
      hideBelow: 'md',
      render: (commission) => formatMoney(commission.base),
    },
    {
      key: 'taxa',
      header: 'Percentual',
      align: 'right',
      hideBelow: 'lg',
      render: (commission) => formatRate(commission.rate_bps),
    },
    {
      key: 'valor',
      header: 'Comissao',
      align: 'right',
      render: (commission) => (
        <span className="font-semibold tabular-nums">
          {formatMoney(commission.amount)}
        </span>
      ),
    },
    {
      key: 'situacao',
      header: 'Situacao',
      render: (commission) => <StatusBadge kind="repasse" value={commission.status} />,
    },
    {
      key: 'acao',
      header: 'Baixa',
      align: 'right',
      render: (commission) =>
        podeBaixar ? (
          <BotaoDeBaixa commission={commission} onPick={setBaixando} />
        ) : null,
    },
  ];

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Comissoes"
        description="Repasses devidos ao corretor, do recebimento ao pagamento."
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              disabled={(consulta.data?.items.length ?? 0) === 0}
              onClick={() =>
                baixarCsv('comissoes', consulta.data?.items ?? [], [
                  { titulo: 'Corretor', valor: (item) => item.broker_label },
                  { titulo: 'Competencia', valor: (item) => item.competence },
                  { titulo: 'Base', valor: (item) => moedaParaPlanilha(item.base) },
                  {
                    titulo: 'Taxa (%)',
                    valor: (item) => (item.rate_bps / 100).toFixed(2).replace('.', ','),
                  },
                  {
                    titulo: 'Comissao',
                    valor: (item) => moedaParaPlanilha(item.amount),
                  },
                  {
                    titulo: 'Situacao',
                    valor: (item) => statusLabel('repasse', item.status),
                  },
                  { titulo: 'Baixa', valor: (item) => item.settled_label },
                  {
                    titulo: 'Origem',
                    valor: (item) => (item.policy_id ? 'Emissao' : 'Lancamento manual'),
                  },
                  {
                    titulo: 'Motivo do lancamento',
                    valor: (item) => item.manual_reason,
                  },
                ])
              }
            >
              Exportar
            </Button>
            {podeLancar ? (
              <Button onClick={() => setLancando(true)}>Nova comissao</Button>
            ) : null}
          </div>
        }
      />

      {consulta.isError ? (
        <Alert tone="danger" title="Nao foi possivel carregar as comissoes">
          {consulta.error.message}
        </Alert>
      ) : null}

      <div className="grid gap-4 sm:grid-cols-3">
        <KpiCard
          icon="clock"
          tone="warning"
          label="A receber"
          value={totais ? formatMoney(totais.pending) : 'R$ 0,00'}
          hint="Ainda nao entrou da seguradora"
          loading={consulta.isPending}
        />
        <KpiCard
          icon="coins"
          tone="info"
          label="Recebida"
          value={totais ? formatMoney(totais.received) : 'R$ 0,00'}
          hint="Entrou e ainda nao foi repassada"
          loading={consulta.isPending}
        />
        <KpiCard
          icon="check"
          tone="success"
          label="Paga"
          value={totais ? formatMoney(totais.paid) : 'R$ 0,00'}
          hint="Repassada ao corretor"
          loading={consulta.isPending}
        />
      </div>

      <Card>
        <div className="flex flex-wrap items-end gap-3 border-b border-line px-5 py-3">
          <Select
            label="Situacao"
            className="h-9"
            options={[
              { value: '', label: 'Todas' },
              { value: 'a_receber', label: 'A receber' },
              { value: 'recebida', label: 'Recebida' },
              { value: 'paga', label: 'Paga' },
              { value: 'cancelada', label: 'Cancelada' },
            ]}
            value={status}
            onChange={(event) => {
              setStatus(event.target.value);
              setOffset(0);
            }}
          />
          <div className="w-40">
            <label className="text-xs font-semibold text-ink-700" htmlFor="competencia">
              Competencia
            </label>
            <input
              id="competencia"
              type="month"
              value={competencia}
              onChange={(event) => {
                setCompetencia(event.target.value);
                setOffset(0);
              }}
              className="mt-1.5 h-9 w-full rounded-md border border-line-strong bg-surface px-3 text-sm text-ink-900 focus:border-brand-500 focus:outline-none focus:ring-3 focus:ring-brand-500/15"
            />
          </div>
        </div>

        <DataTable
          columns={colunas}
          items={consulta.data?.items ?? []}
          keyOf={(commission) => commission.id}
          loading={consulta.isPending}
          empty={
            <EmptyState
              icon="coins"
              title={
                status || competencia
                  ? 'Nenhuma comissao encontrada'
                  : 'Nenhuma comissao lancada'
              }
              description={
                status || competencia
                  ? 'Ajuste os filtros para ver outras competencias.'
                  : 'A comissao e lancada junto com a emissao da apolice. Ela aparece aqui assim que a primeira garantia for emitida.'
              }
            />
          }
        />

        <Pagination
          total={consulta.data?.total ?? 0}
          limit={limite}
          offset={offset}
          onChange={setOffset}
        />
      </Card>

      {lancando ? (
        <LancamentoManual
          submitting={lancar.isPending}
          error={lancar.error}
          onCancel={() => setLancando(false)}
          onSubmit={(input) => lancar.mutate(input)}
        />
      ) : null}

      <ConfirmDialog
        open={baixando !== null}
        onClose={() => setBaixando(null)}
        onConfirm={() =>
          baixando &&
          baixar.mutate({ id: baixando.commission.id, target: baixando.target })
        }
        title={
          baixando?.target === 'recebida'
            ? 'Confirmar recebimento'
            : 'Confirmar pagamento'
        }
        message={
          baixando
            ? `${formatMoney(baixando.commission.amount)} de ${baixando.commission.broker_label || 'operacao direta'}. A baixa registra a data e o autor, e nao pode ser desfeita.`
            : ''
        }
        confirmLabel="Confirmar baixa"
        loading={baixar.isPending}
      />
    </div>
  );
}

/**
 * Botao de baixa do repasse.
 *
 * Ele oferece apenas o proximo passo possivel. Um repasse ja pago nao mostra
 * botao: o caminho e so para a frente, e oferecer uma acao que o servidor
 * recusaria so produziria erro.
 */
function BotaoDeBaixa({
  commission,
  onPick,
}: {
  commission: Commission;
  onPick: (escolha: { commission: Commission; target: CommissionStatus }) => void;
}) {
  if (commission.status === 'a_receber') {
    return (
      <Button
        size="sm"
        variant="secondary"
        onClick={() => onPick({ commission, target: 'recebida' })}
      >
        Marcar recebida
      </Button>
    );
  }
  if (commission.status === 'recebida') {
    return (
      <Button size="sm" onClick={() => onPick({ commission, target: 'paga' })}>
        Marcar paga
      </Button>
    );
  }
  return <Badge tone="neutral">encerrada</Badge>;
}

/**
 * Carteira de fiancas bancarias.
 *
 * Mesma tela da carteira de apolices, com o corte pela contraparte que emitiu.
 */
export function BankGuaranteesPage() {
  return (
    <PoliciesPage
      issuerKind="banco"
      titulo="Fiancas bancarias"
      descricao="Cartas de fianca emitidas junto aos bancos parceiros."
      vazioTitulo="Nenhuma fianca emitida"
      vazio="Uma operacao emitida com banco como contraparte aparece aqui."
    />
  );
}

/**
 * Lancamento manual de comissao.
 *
 * Existe para o repasse que nao nasce de uma emissao: acerto de competencia
 * anterior, correcao e negocio trazido fora da esteira. Sem ele, esses casos acabam
 * em planilha, e o total da tela deixa de ser o total do que a empresa deve.
 *
 * O valor sai da base e da taxa, e nao e digitado: um valor que nao fecha com os dois
 * e uma conta que ninguem consegue refazer depois.
 */
function LancamentoManual({
  submitting,
  error,
  onCancel,
  onSubmit,
}: {
  submitting: boolean;
  error: Error | null;
  onCancel: () => void;
  onSubmit: (input: ManualCommissionInput) => void;
}) {
  const [corretor, setCorretor] = useState('');
  const [base, setBase] = useState('');
  const [taxa, setTaxa] = useState('');
  const [competencia, setCompetencia] = useState('');
  const [motivo, setMotivo] = useState('');

  const listaCorretores = useQuery({
    queryKey: ['brokers', 'options'],
    queryFn: ({ signal }) => brokers.list({ limit: 100, stage: 'ativo' }, signal),
  });

  const apiError = error instanceof ApiError ? error : null;
  const erroBase = moneyInputError(base);
  const erroTaxa = rateInputError(taxa);
  const invalido = erroBase !== undefined || erroTaxa !== undefined;

  // O calculo aparece enquanto se digita, com a mesma conta do servidor. Ver o
  // resultado antes de gravar evita o lancamento com a taxa no campo errado. A base
  // e lida pela mesma conversao que vai ao servidor: com conversoes diferentes, a
  // previa mostrava o valor certo e a gravacao levava dez ou cem vezes mais.
  const previa = (() => {
    const valorBase = parseMoney(base);
    const pontos = rateToBasisPoints(taxa || '0');
    if (!valorBase || valorBase.amount <= 0 || pontos <= 0) return null;
    return applyRate(valorBase, pontos);
  })();

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    if (invalido) return;
    onSubmit({
      broker_id: corretor,
      base: toMoney(base || '0'),
      rate_bps: rateToBasisPoints(taxa || '0'),
      competence: competencia,
      manual_reason: motivo,
    });
  }

  return (
    <Modal open onClose={onCancel} title="Nova comissao" size="md">
      <form onSubmit={enviar} className="flex flex-col gap-4">
        {error ? (
          <Alert tone="danger" title="Nao foi possivel lancar">
            {error.message}
          </Alert>
        ) : null}

        <Select
          label="Corretor"
          required
          placeholder="Selecione quem recebe"
          value={corretor}
          options={(listaCorretores.data?.items ?? []).map((item) => ({
            value: item.id,
            label: item.legal_name,
          }))}
          error={fieldErrorOf(apiError, 'broker_id')}
          onChange={(evento) => setCorretor(evento.target.value)}
        />

        <div className="grid gap-4 sm:grid-cols-2">
          {/* Texto, e nao campo numerico: o campo numerico do navegador devolve o
              ponto como decimal e nao aceita a virgula que o restante da tela usa. */}
          <MoneyField
            label="Base de calculo (R$)"
            required
            value={base}
            error={erroBase ?? fieldErrorOf(apiError, 'base')}
            onValueChange={setBase}
          />
          <Field
            label="Percentual (%)"
            inputMode="decimal"
            required
            value={taxa}
            error={erroTaxa ?? fieldErrorOf(apiError, 'rate_bps')}
            onChange={(evento) => setTaxa(evento.target.value)}
          />
        </div>

        {previa !== null ? (
          <p className="text-xs text-ink-600">
            Comissao calculada: <b>{formatMoney(previa)}</b>
          </p>
        ) : null}

        <Field
          label="Competencia"
          placeholder="AAAA-MM"
          value={competencia}
          hint="Vazio assume o mes corrente."
          error={fieldErrorOf(apiError, 'competence')}
          onChange={(evento) => setCompetencia(evento.target.value)}
        />

        <Textarea
          label="Motivo do lancamento"
          required
          value={motivo}
          hint="Sem o motivo, uma comissao sem apolice fica indistinguivel de um erro de gravacao."
          error={fieldErrorOf(apiError, 'manual_reason')}
          onChange={(evento) => setMotivo(evento.target.value)}
        />

        <div className="flex justify-end gap-2 border-t border-line pt-4">
          <Button type="button" variant="secondary" onClick={onCancel}>
            Cancelar
          </Button>
          <Button type="submit" loading={submitting} disabled={invalido}>
            Lancar comissao
          </Button>
        </div>
      </form>
    </Modal>
  );
}
